import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Shield, Zap } from "lucide-react";
import { toast } from "sonner";
import CaprocureLogo from "@/components/CaprocureLogo";

// All existing demo accounts (matches the demo seed in /app/backend/server.py).
// `quickLogin: false` means the row only fills the email field (used for the
// real Admin account so we never assume/expose a specific admin password).
const DEMO_ACCOUNTS = [
  { role: "Admin",            name: "Caprocure Admin",         company: "Caprocure",                    email: "jmohatla@gmail.com",       pw: "",              quickLogin: false },
  { role: "Buyer",            name: "Emma Tremblay",          company: "Northline Manufacturing Inc.",email: "buyer@caprocure.demo",     pw: "Buyer@123",     quickLogin: true },
  { role: "Buyer",            name: "Liam Bergeron",          company: "Boreal Foods Ltd.",           email: "buyer2@caprocure.demo",    pw: "Buyer@123",     quickLogin: true },
  { role: "Supplier",         name: "Ravi Sharma",            company: "Maple Industrial Supply Co.", email: "supplier@caprocure.demo",  pw: "Supplier@123",  quickLogin: true },
  { role: "Supplier",         name: "Isabelle Moreau",        company: "Rocky Mountain Equipment",    email: "supplier2@caprocure.demo", pw: "Supplier@123",  quickLogin: true },
  { role: "Supplier",         name: "Chen Wei",               company: "Pacific Trade Solutions",     email: "supplier3@caprocure.demo", pw: "Supplier@123",  quickLogin: true },
  { role: "Account Manager",  name: "Sophie Laurent",         company: "Caprocure",                    email: "am@caprocure.demo",        pw: "Manager@123",   quickLogin: true },
];

// Dev-only gate — hidden on production builds AND on any published host that is
// not localhost / the Emergent preview domain. Two independent checks so a
// misconfigured build still can't leak the panel into production.
const isDevPreview = () => {
  if (process.env.NODE_ENV === "production") return false;
  if (typeof window === "undefined") return false;
  const host = window.location.hostname || "";
  return host === "localhost"
    || host.startsWith("127.")
    || host.endsWith(".preview.emergentagent.com")
    || host.endsWith(".emergent.host");
};

const ROLE_BADGE = {
  "Admin":            "bg-[#0A2540] text-white",
  "Buyer":            "bg-sky-100 text-sky-800",
  "Supplier":         "bg-amber-100 text-amber-800",
  "Account Manager":  "bg-violet-100 text-violet-800",
};

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [quickLoggingId, setQuickLoggingId] = useState(null);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const showDemo = isDevPreview();

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true); setErr("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/app");
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  };

  const quickLogin = async (d) => {
    setErr("");
    if (!d.quickLogin) {
      // For accounts we won't auto-authenticate (real Admin), just prefill the
      // email and focus the password field. Never store or assume a password.
      setEmail(d.email);
      setPassword("");
      toast.info(`Email filled — type your ${d.role.toLowerCase()} password`);
      setTimeout(() => {
        const el = document.getElementById("password");
        if (el) el.focus();
      }, 50);
      return;
    }
    setQuickLoggingId(d.email);
    try {
      await login(d.email, d.pw);
      toast.success(`Signed in as ${d.name}`);
      nav("/app");
    } catch (e) {
      setErr(e.message || "Quick login failed");
      toast.error(e.message || "Quick login failed");
    } finally {
      setQuickLoggingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className={`max-w-6xl w-full grid gap-8 ${showDemo ? "md:grid-cols-[1.1fr_1fr]" : "md:grid-cols-2"}`}>
        {/* Left brand panel — kept for both dev and prod */}
        <div className="hidden md:flex flex-col justify-between bg-[#0A2540] text-white rounded-2xl p-10">
          <div>
            <Link to="/" aria-label="Caprocure home">
              <CaprocureLogo variant="dark" size="md" />
            </Link>
            <div className="mt-14 eyebrow text-blue-200">Access the platform</div>
            <h1 className="font-display text-4xl font-extrabold tracking-tight mt-2 leading-tight">
              Connecting Canadian Buyers with Trusted Suppliers.
            </h1>
            <p className="mt-4 text-blue-100/70 text-sm max-w-sm">
              Buyers, suppliers and Caprocure account managers — one workflow, four dashboards.
            </p>
          </div>
          <div className="text-xs text-blue-200/50 mt-8">
            © {new Date().getFullYear()} JMB Investment Group Ltd., operating as Caprocure™
          </div>
        </div>

        {/* Right column — either normal login OR (in dev) login + demo switcher */}
        <div className="space-y-6">
          <Card className="p-10 border-slate-200">
            <div className="eyebrow mb-2">Sign in</div>
            <h2 className="font-display text-2xl font-bold text-[#0A2540]">Welcome back</h2>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="email">Work email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
              </div>
              {err && <div className="text-sm text-rose-600" data-testid="login-error">{err}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] hover:bg-[#061A2E]" data-testid="login-submit-btn">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
              <div className="text-sm text-slate-600 text-center">
                No account? <Link to="/register" className="text-[#1D4ED8] font-medium">Register</Link>
              </div>
              <div className="text-xs text-slate-500 text-center">
                Forgot your password? <Link to="/forgot-password" className="text-[#1D4ED8] font-medium" data-testid="forgot-password-link">Reset it here</Link>
              </div>
              <div className="text-xs text-slate-400 text-center">
                Admin lockout? <Link to="/recover-admin" className="text-slate-500 hover:text-[#1D4ED8] font-medium" data-testid="recover-admin-link">Recover admin access</Link>
              </div>
            </form>
          </Card>

          {showDemo && (
            <Card className="p-6 border-amber-200 bg-amber-50/30" data-testid="demo-switcher-panel">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-amber-600" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Dev only · Not shown in production</span>
              </div>
              <h3 className="font-display font-bold text-[#0A2540]">Demo account switcher</h3>
              <p className="text-xs text-slate-500 mt-1 mb-4">One-click sign-in for every seeded demo persona. Use these while testing role permissions and workflows.</p>
              <div className="grid grid-cols-1 gap-2">
                {DEMO_ACCOUNTS.map((d) => {
                  const busy = quickLoggingId === d.email;
                  const testid = `demo-login-${d.email.split("@")[0]}`;
                  return (
                    <button
                      key={d.email}
                      onClick={() => quickLogin(d)}
                      disabled={!!quickLoggingId}
                      data-testid={testid}
                      className="text-left flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-slate-200 bg-white hover:border-[#0A2540] hover:shadow-sm transition-all disabled:opacity-60 disabled:cursor-wait"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${ROLE_BADGE[d.role] || "bg-slate-100 text-slate-700"}`}>{d.role}</span>
                          <span className="text-sm font-medium text-[#0A2540] truncate">{d.name}</span>
                        </div>
                        <div className="text-xs text-slate-500 truncate mt-0.5">{d.company} · {d.email}</div>
                      </div>
                      <span className="text-xs font-semibold text-[#1D4ED8] whitespace-nowrap">{busy ? "Signing in…" : (d.quickLogin ? "Sign in →" : "Fill email →")}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 text-[11px] text-slate-500 leading-relaxed">
                Password-based login above still works with the same credentials. This panel simply automates it for development testing and disappears on production builds.
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
